import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { AppContext } from '../../contexts/AppContext';
import { ThemeContext, ThemeContextData } from "../../contexts/ThemeContext";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const BlogDetailPage = () => {
    const { blogId } = useParams();
    const appContext = React.useContext(AppContext);
    const themeContext = React.useContext(ThemeContext);
    const theme = ThemeContextData[themeContext];
    const navigate = useNavigate();

    // Find the blog by ID
    const blog = appContext.blogsData.allBlogs.find(b => b.id === parseInt(blogId));

    // Handle case if blog is not found
    useEffect(() => {
        if (!blog) {
            navigate('/error'); // Navigate to error page if blog not found
        }
        
        // Scroll to top when component mounts
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [blog, navigate]);

    if (!blog) return null; // Don't render anything if blog not found

    return (
        <section className={`${theme.textNormal} ${theme.background} body-font`}>
            <div className="container mx-auto px-5 py-12">
                {/* Blog content */}
                <div className={`prose max-w-none lg:prose-lg ${theme.textNormal}`}>
                    <ReactMarkdown 
                        remarkPlugins={[remarkGfm]}
                        components={{
                            p: ({ node, ...props }) => <p className="mb-4" {...props} />,
                            h1: ({ node, ...props }) => <h1 className={`${theme.textFocus} text-3xl font-bold mb-4 mt-8`} {...props} />,
                            h2: ({ node, ...props }) => <h2 className={`${theme.textFocus} text-2xl font-bold mb-3 mt-6`} {...props} />,
                            h3: ({ node, ...props }) => <h3 className={`${theme.textFocus} text-xl font-bold mb-3 mt-5`} {...props} />,
                            h4: ({ node, ...props }) => <h4 className={`${theme.textFocus} text-lg font-bold mb-2 mt-4`} {...props} />,
                            ul: ({ node, ...props }) => <ul className="list-disc list-outside mb-4 pl-5" {...props} />,
                            ol: ({ node, ...props }) => <ol className="list-decimal list-outside mb-4 pl-5" {...props} />,
                            li: ({ node, ...props }) => <li className="mb-1" {...props} />,
                            a: ({ node, ...props }) => <a className={`${theme.themeColour}-500 hover:${theme.themeColour}-700 underline`} {...props} />,
                            blockquote: ({ node, ...props }) => <blockquote className={`border-l-4 border-${theme.themeColour}-500 pl-4 italic my-4`} {...props} />,
                            img: ({ node, ...props }) => <img className="my-6 rounded-lg shadow-md max-w-full h-auto" {...props} />,
                            table: ({ node, ...props }) => <div className="overflow-x-auto my-6"><table className="min-w-full divide-y divide-gray-300" {...props} /></div>,
                            th: ({ node, ...props }) => <th className={`${theme.backgroundTertiary} px-3 py-2 text-left font-semibold`} {...props} />,
                            td: ({ node, ...props }) => <td className="px-3 py-2 border-t border-gray-200" {...props} />,
                            code: ({ node, inline, ...props }) => 
                                inline ? (
                                    <code className={`${theme.backgroundTertiary} px-1 py-0.5 rounded text-sm`} {...props} />
                                ) : (
                                    <code className={`${theme.backgroundTertiary} block p-4 rounded-lg mb-4 overflow-x-auto text-sm`} {...props} />
                                ),
                        }}
                    >
                        {blog.excerpt}
                    </ReactMarkdown>
                </div>

                {/* Back navigation at the bottom */}
                <div className="mt-12 pt-6 border-t border-gray-200">
                    <Link 
                        to="/blogs" 
                        className={`inline-flex items-center ${theme.buttonPrimary} px-4 py-2 rounded`}
                    >
                        <svg 
                            fill="none" 
                            stroke="currentColor" 
                            strokeLinecap="round" 
                            strokeLinejoin="round" 
                            strokeWidth="2" 
                            className="w-4 h-4 mr-2" 
                            viewBox="0 0 24 24"
                        >
                            <path d="M19 12H5M12 19l-7-7 7-7"></path>
                        </svg>
                        Back to All Blogs
                    </Link>
                </div>
            </div>
        </section>
    );
};

export default BlogDetailPage;
